import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

// Square logo cropper. Opens after the owner picks an image: they pan/zoom to
// frame the logo inside a circular (square) mask, then "Apply" returns a
// center-square, compressed JPEG File ready to upload.
const OUTPUT_SIZE = 500; // px (square)

const getCroppedFile = (imageSrc, cropPixels, fileName) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Crop failed'));
            return;
          }
          const base = (fileName || 'logo').replace(/\.[^.]+$/, '');
          const file = new File([blob], base + '.jpg', { type: 'image/jpeg' });
          resolve({ file, preview: canvas.toDataURL('image/jpeg', 0.85) });
        },
        'image/jpeg',
        0.85
      );
    };
    image.onerror = reject;
    image.src = imageSrc;
  });

const LogoCropModal = ({ open, imageSrc, fileName, onCancel, onApply }) => {
  // [CF 2026-08-17] This dialog had never been wired for translation at all.
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const result = await getCroppedFile(imageSrc, croppedAreaPixels, fileName);
      onApply(result);
    } catch (e) {
      onApply(null);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '14px 18px', fontWeight: 600, fontSize: '16px' }}>
          {t('Adjust logo')}
        </div>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '300px',
            background: '#333',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div style={{ padding: '14px 18px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              color: '#666',
              marginBottom: '6px',
            }}
          >
            {t('Zoom')}
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: '100%' }}
          />
          <div
            style={{
              display: 'flex',
              gap: '10px',
              marginTop: '16px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onCancel}
              disabled={busy}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleApply}
              disabled={busy}
            >
              {busy ? t('Saving…') : t('Apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoCropModal;
